/**
 * Starter PageHeader — the standard page-title block.
 *
 * Renders an optional uppercase eyebrow, a heading (defaults to a
 * level-1 display heading so the brand voice carries), an optional
 * subtitle, and an `actions` slot aligned to the right (theme toggle,
 * primary CTA, etc).
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Display, Heading } from './typography';
import styles from './PageHeader.module.css';

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Optional uppercase eyebrow above the title ("MORNING BRIEFING"). */
  readonly eyebrow?: ReactNode;
  /** The page title. Pass any ReactNode (often `Good morning, <em>Mathew</em>`). */
  readonly title: ReactNode;
  /** Optional subtitle / supporting copy below the title. */
  readonly subtitle?: ReactNode;
  /** Right-aligned action slot — buttons, toggles, etc. */
  readonly actions?: ReactNode;
  /** Heading level for the `heading` variant. Defaults to `1` (Libre Baskerville, the display face). */
  readonly headingLevel?: 1 | 2;
  /**
   * Title typographic treatment:
   *  - `heading` (default) — the Libre Baskerville heading at `headingLevel` scale.
   *  - `display` — the Libre Baskerville Display primitive (with the Cormorant Garamond
   *    <em> accent), the design system's signature greeting treatment
   *    (e.g. "Good morning, <em>Mathew</em>").
   */
  readonly titleVariant?: 'heading' | 'display';
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  function PageHeader(
    {
      eyebrow,
      title,
      subtitle,
      actions,
      headingLevel = 1,
      titleVariant = 'heading',
      className,
      ...rest
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={[styles.root, className].filter(Boolean).join(' ')}
        {...rest}
      >
        <div className={styles.left}>
          {eyebrow !== undefined ? (
            <span className={styles.eyebrow}>{eyebrow}</span>
          ) : null}
          {titleVariant === 'display' ? (
            <Display>{title}</Display>
          ) : (
            <Heading level={headingLevel}>{title}</Heading>
          )}
          {subtitle !== undefined ? (
            <div className={styles.subtitle}>{subtitle}</div>
          ) : null}
        </div>
        {actions !== undefined ? (
          <div className={styles.actions}>{actions}</div>
        ) : null}
      </div>
    );
  },
);
